import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";

/**
 * Parent-managed tasks — kids on task-gated mode must complete these
 * (with photo verification) before their daily game-minutes quota unlocks.
 * Backend: /api/tasks (CRUD), /api/kids/{id}/access-mode (toggle).
 */
export default function Tasks() {
  const [tasks, setTasks] = useState([]);
  const [kids, setKids] = useState([]);
  const [tokenTypes, setTokenTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | "new" | task

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    try {
      const [t, k, tt] = await Promise.all([api.get("/tasks"), api.get("/kids"),
                                            api.get("/tokens").catch(() => ({ data: [] }))]);
      setTasks(t.data);
      setKids(k.data);
      setTokenTypes(tt.data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    } finally { setLoading(false); }
  }

  async function updateKidMode(kid, mode, minutes) {
    try {
      await api.post(`/kids/${kid.id}/access-mode`, {
        access_mode: mode,
        daily_game_minutes: minutes != null ? minutes : kid.daily_game_minutes,
      });
      toast.success(`${kid.name} is now on ${mode === "task_gated" ? "task-gated" : "schedule"} mode`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    }
  }

  return (
    <div className="space-y-6" data-testid="tasks-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-[32px] md:text-[38px] font-bold tracking-tight text-[#2C342A]">Jobs</h1>
          <p className="text-sm text-[#707A6E] mt-1">
            Jobs for the kids: pick the days and a "do it by" time and they show in the kid's What's on (not in your
            Outlook calendar). Task-based kids must finish them first; then their normal times
            and holidays apply. Jobs can earn extra minutes or
            a token.
          </p>
        </div>
        <button data-testid="add-task-btn" onClick={() => setEditing("new")}
                className="rounded-xl bg-[#2C342A] px-4 py-2 text-sm font-medium text-[#F5F4F0] hover:bg-[#2C342A]">
          + Add task
        </button>
      </div>

      {/* Per-kid mode toggle */}
      <div className="rounded-2xl bg-[#FAFAF7] border border-[#F0EFEB] p-5">
        <h3 className="font-heading text-lg mb-3">Access mode per child</h3>
        <div className="space-y-2">
          {kids.map((k) => (
            <div key={k.id} className="flex items-center justify-between rounded-lg bg-white/60 border border-[#F0EFEB] p-3"
                 data-testid={`kid-mode-${k.id}`}>
              <div>
                <div className="font-medium">{k.name}</div>
                <div className="text-xs text-[#707A6E]">
                  {k.access_mode === "task_gated"
                    ? "Tasks first, then their normal times and holidays"
                    : "Their normal times and holidays (default)"}
                </div>
              </div>
              <div className="flex gap-2 items-center">
                <button
                  data-testid={`kid-toggle-${k.id}`}
                  onClick={() => updateKidMode(k, k.access_mode === "task_gated" ? "schedule" : "task_gated")}
                  className={`rounded-lg px-3 py-1.5 text-sm ${k.access_mode === "task_gated" ? "bg-[#6B9F80] text-white" : "bg-[#F0EFEB]"}`}>
                  {k.access_mode === "task_gated" ? "Task-gated ON" : "Enable task-gated"}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Tasks list */}
      <div className="rounded-2xl bg-[#FAFAF7] border border-[#F0EFEB] p-5">
        <h3 className="font-heading text-lg mb-3">Task list ({tasks.length})</h3>
        {loading && <div className="text-sm text-[#707A6E]">Loading…</div>}
        {!loading && tasks.length === 0 && (
          <div className="text-sm text-[#707A6E]">No tasks yet. Add one above.</div>
        )}
        <div className="space-y-2">
          {tasks.map((t) => (
            <button key={t.id} onClick={() => setEditing(t)}
                    className="w-full text-left rounded-lg bg-white/60 border border-[#F0EFEB] p-3 hover:bg-white"
                    data-testid={`task-${t.id}`}>
              <div className="flex items-center gap-3">
                <div className="text-2xl">{t.emoji}</div>
                <div className="flex-1">
                  <div className="font-medium">{t.title}</div>
                  <div className="text-xs text-[#707A6E]">
                    {t.kid_ids.includes("all") ? "All kids" : `${t.kid_ids.length} kid${t.kid_ids.length === 1 ? "" : "s"}`}
                    {` · ${t.days_label || "Every day"}`}
                    {t.due_time && ` by ${t.due_time}`}
                    {t.reward_minutes > 0 && ` · +${t.reward_minutes} min`}
                    {t.photo_required === false && " · No photo"}
                    {t.gates === false && " · Doesn't lock screens"}
                    {!t.active && " · Inactive"}
                  </div>
                </div>
                <div className="text-[#707A6E]">›</div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {editing && (
        <TaskEditor
          initial={editing === "new" ? null : editing}
          kids={kids}
          tokenTypes={tokenTypes}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

const DAY_NAMES = ["M", "T", "W", "T", "F", "S", "S"];

function TaskEditor({ initial, kids, tokenTypes, onClose, onSaved }) {
  const [days, setDays] = useState(initial?.days || []);
  const [dueTime, setDueTime] = useState(initial?.due_time || "");
  const [showInCalendar, setShowInCalendar] = useState(initial?.show_in_calendar ?? true);
  const [remind, setRemind] = useState(initial?.remind_minutes || 0);
  const [rewardMinutes, setRewardMinutes] = useState(initial?.reward_minutes || 0);
  const [rewardToken, setRewardToken] = useState(initial?.reward_token_type_id || "");
  const [photo, setPhoto] = useState(initial?.photo_required ?? true);
  const [gates, setGates] = useState(initial?.gates ?? true);
  const [title, setTitle] = useState(initial?.title || "");
  const [emoji, setEmoji] = useState(initial?.emoji || "✅");
  const [description, setDescription] = useState(initial?.description || "");
  const [active, setActive] = useState(initial?.active ?? true);
  const [applyMode, setApplyMode] = useState(
    initial?.kid_ids?.includes("all") || !initial ? "all" : "specific"
  );
  const [kidIds, setKidIds] = useState(
    initial?.kid_ids?.filter(x => x !== "all") || []
  );
  const [saving, setSaving] = useState(false);

  async function save() {
    setSaving(true);
    try {
      const body = {
        title, emoji, description, active,
        kid_ids: applyMode === "all" ? ["all"] : kidIds,
        days, due_time: dueTime || null, show_in_calendar: showInCalendar,
        remind_minutes: remind > 0 && dueTime ? remind : null,
        reward_minutes: Number(rewardMinutes) || 0, reward_token_type_id: rewardToken || null,
        photo_required: photo, gates,
      };
      if (initial?.id) await api.patch(`/tasks/${initial.id}`, body);
      else await api.post("/tasks", body);
      toast.success(initial?.id ? "Task updated" : "Task created");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    } finally { setSaving(false); }
  }

  async function remove() {
    if (!initial?.id) return;
    if (!confirm("Delete this task?")) return;
    setSaving(true);
    try {
      await api.delete(`/tasks/${initial.id}`);
      toast.success("Task deleted");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    } finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="w-full max-w-lg max-h-[92vh] overflow-y-auto rounded-2xl bg-[#FAFAF7] p-6 space-y-4" onClick={(e) => e.stopPropagation()}
           data-testid="task-editor">
        <h3 className="font-heading text-xl">{initial?.id ? "Edit task" : "New task"}</h3>
        <div className="grid grid-cols-[auto,1fr] gap-2">
          <input value={emoji} onChange={(e) => setEmoji(e.target.value)}
                 className="w-16 rounded-lg border border-[#F0EFEB] px-3 py-2 text-center bg-white" data-testid="task-emoji" />
          <input value={title} onChange={(e) => setTitle(e.target.value)}
                 placeholder="Task title (e.g. Make your bed)"
                 className="rounded-lg border border-[#F0EFEB] px-3 py-2 bg-white" data-testid="task-title" />
        </div>
        <textarea value={description} onChange={(e) => setDescription(e.target.value)}
                  placeholder="What does the kid need to photograph? (optional)"
                  className="w-full rounded-lg border border-[#F0EFEB] px-3 py-2 bg-white text-sm" rows={2}
                  data-testid="task-description" />
        <div className="text-sm">
          <div className="text-[#707A6E] mb-1">Applies to</div>
          <div className="flex gap-2 mb-2">
            <button onClick={() => setApplyMode("all")}
                    className={`rounded-full px-3 py-1 text-xs ${applyMode === "all" ? "bg-[#2C342A] text-[#F5F4F0]" : "bg-[#F0EFEB]"}`}
                    data-testid="task-apply-all">All kids</button>
            <button onClick={() => setApplyMode("specific")}
                    className={`rounded-full px-3 py-1 text-xs ${applyMode === "specific" ? "bg-[#2C342A] text-[#F5F4F0]" : "bg-[#F0EFEB]"}`}
                    data-testid="task-apply-specific">Specific kids</button>
          </div>
          {applyMode === "specific" && (
            <div className="flex flex-wrap gap-2">
              {kids.map((k) => (
                <button key={k.id}
                        onClick={() => setKidIds((prev) => prev.includes(k.id) ? prev.filter(x => x !== k.id) : [...prev, k.id])}
                        className={`rounded-full px-3 py-1 text-xs ${kidIds.includes(k.id) ? "bg-[#6B9F80] text-white" : "bg-white border border-[#F0EFEB]"}`}
                        data-testid={`task-kid-${k.id}`}>{k.name}</button>
              ))}
            </div>
          )}
        </div>
        <div className="text-sm space-y-2">
          <div className="text-[#707A6E]">When</div>
          <div className="flex gap-1.5" data-testid="task-days">
            {DAY_NAMES.map((d, i) => (
              <button key={i} type="button"
                      onClick={() => setDays((prev) => prev.includes(i) ? prev.filter(x => x !== i) : [...prev, i].sort())}
                      className={`h-9 w-9 rounded-full text-xs font-semibold ${days.length === 0 || days.includes(i) ? "bg-[#5C7A4F] text-white" : "bg-[#F0EFEB] text-[#707A6E]"}`}>
                {d}
              </button>
            ))}
            {days.length > 0 && (
              <button type="button" onClick={() => setDays([])} className="ml-2 text-xs underline text-[#707A6E]">Every day</button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <span>Do it by</span>
            <input type="time" value={dueTime} onChange={(e) => setDueTime(e.target.value)}
                   className="rounded-lg border border-[#F0EFEB] px-2 py-1 bg-white" data-testid="task-due" />
            {dueTime && <button type="button" onClick={() => setDueTime("")} className="text-xs underline text-[#707A6E]">No time</button>}
          </div>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={showInCalendar} onChange={(e) => setShowInCalendar(e.target.checked)} />
            Show in their calendar (What's on). Not added to your Outlook calendar.
          </label>
          {dueTime && (
            <label className="flex items-center gap-2">
              Remind them
              <select value={remind} onChange={(e) => setRemind(Number(e.target.value))}
                      className="rounded-lg border border-[#F0EFEB] px-2 py-1 bg-white">
                <option value={0}>No reminder</option>
                {[10, 15, 30, 60].map((m) => <option key={m} value={m}>{m} min before</option>)}
              </select>
            </label>
          )}
        </div>
        <div className="text-sm space-y-2">
          <div className="text-[#707A6E]">Reward</div>
          <label className="flex items-center gap-2">
            Extra time
            <select value={rewardMinutes} onChange={(e) => setRewardMinutes(Number(e.target.value))}
                    className="rounded-lg border border-[#F0EFEB] px-2 py-1 bg-white" data-testid="task-reward-minutes">
              {[0, 5, 10, 15, 20, 30, 45, 60].map((m) => <option key={m} value={m}>{m === 0 ? "None" : `${m} min`}</option>)}
            </select>
          </label>
          {tokenTypes.length > 0 && (
            <label className="flex items-center gap-2">
              Token
              <select value={rewardToken} onChange={(e) => setRewardToken(e.target.value)}
                      className="rounded-lg border border-[#F0EFEB] px-2 py-1 bg-white">
                <option value="">None</option>
                {tokenTypes.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </label>
          )}
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={photo} onChange={(e) => setPhoto(e.target.checked)} />
          Needs a photo to prove it's done
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={gates} onChange={(e) => setGates(e.target.checked)} />
          Task-based kids: screens stay locked until it's done
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} data-testid="task-active" />
          Active (kids see it in their task list)
        </label>
        <div className="flex justify-between pt-2">
          <div>
            {initial?.id && (
              <button onClick={remove} disabled={saving} className="rounded-lg px-4 py-2 text-sm text-[#C06E52] hover:bg-[#F6E3DC]" data-testid="task-delete">
                Delete
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} className="rounded-lg px-4 py-2 text-sm hover:bg-[#F0EFEB]" data-testid="task-cancel">Cancel</button>
            <button onClick={save} disabled={saving || !title} className="rounded-lg bg-[#2C342A] px-4 py-2 text-sm text-[#F5F4F0]" data-testid="task-save">
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
